// spriteCode.ts - deterministic code round trip for Pixel Studio sprites.
// "Export to code" writes a sprite out as plain, diffable JSON; "Open in
// Studio" reads it back in with full fidelity. See docs/sprite-pipeline
// for the design behind this (deterministic serialisation, why JSON rather
// than a hand-formatted .ts literal).
import { migratePixelSpriteDoc, type PixelSpriteDoc } from './editor-pixel';

const SPRITE_CODE_FORMAT = 'sstk-sprite';
const SPRITE_CODE_VERSION = 1;

export class SpriteCodeError extends Error {}

/**
 * Deterministic: exporting the same sprite twice with no edits produces
 * byte-identical text. Key order is fixed by this function (never derived
 * from object key enumeration order of untrusted input) and indentation is
 * always two spaces.
 */
export function exportSpriteToCode(id: string, sprite: PixelSpriteDoc): string {
  const doc = {
    format: SPRITE_CODE_FORMAT,
    version: SPRITE_CODE_VERSION,
    id,
    size: sprite.size,
    activeClip: sprite.activeClip,
    clips: Object.fromEntries(
      Object.entries(sprite.clips).map(([name, clip]) => [
        name,
        { fps: clip.fps, loop: clip.loop, frames: clip.frames },
      ]),
    ),
  };
  return JSON.stringify(doc, null, 2);
}

function fail(message: string): never {
  throw new SpriteCodeError(message);
}

function isCellRow(value: unknown, size: number): value is (string | null)[] {
  return Array.isArray(value) && value.length === size * size && value.every((c) => c === null || typeof c === 'string');
}

/**
 * Parse a sprite code file back into an id and a current-shape sprite doc.
 * Accepts both the current {clips, activeClip} shape and the older
 * {frames, fps} shape a hand-authored or pre-clips file might still use,
 * upgrading the latter through the same migration the editor itself uses.
 * Throws SpriteCodeError with a message suitable for showing directly to
 * the person importing the file.
 */
export function parseSpriteCode(text: string): { id: string; sprite: PixelSpriteDoc } {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    fail('That file is not valid JSON.');
  }
  if (typeof raw !== 'object' || raw === null) fail('That file does not contain a sprite.');
  const data = raw as Record<string, unknown>;
  if (data.format !== SPRITE_CODE_FORMAT) fail('That file is not a Soulstack Studio sprite (missing or wrong "format").');
  if (typeof data.id !== 'string' || !data.id) fail('That sprite file is missing an "id".');
  if (typeof data.size !== 'number' || !Number.isInteger(data.size) || data.size <= 0) fail('That sprite file has an invalid "size".');
  const size = data.size;

  if (data.clips && typeof data.clips === 'object') {
    const clipsIn = data.clips as Record<string, unknown>;
    const clipNames = Object.keys(clipsIn);
    if (clipNames.length === 0) fail('That sprite file has no clips.');
    const clips: PixelSpriteDoc['clips'] = {};
    for (const name of clipNames) {
      const c = clipsIn[name] as Record<string, unknown> | null;
      if (!c || typeof c !== 'object') fail('Clip "' + name + '" is not a valid clip.');
      if (!Array.isArray(c.frames) || c.frames.length === 0 || !c.frames.every((f) => isCellRow(f, size))) {
        fail('Clip "' + name + '" has invalid frame data for a ' + size + 'x' + size + ' sprite.');
      }
      if (typeof c.fps !== 'number' || c.fps <= 0) fail('Clip "' + name + '" has an invalid "fps".');
      if (typeof c.loop !== 'boolean') fail('Clip "' + name + '" has an invalid "loop".');
      clips[name] = { frames: c.frames as (string | null)[][], fps: c.fps, loop: c.loop };
    }
    const activeClip = typeof data.activeClip === 'string' && clips[data.activeClip] ? data.activeClip : clipNames[0]!;
    return { id: data.id, sprite: { size, clips, activeClip } };
  }

  // Legacy shape: a single flat flipbook, no named clips.
  if (Array.isArray(data.frames) && data.frames.length > 0 && data.frames.every((f) => isCellRow(f, size))) {
    const fps = typeof data.fps === 'number' && data.fps > 0 ? data.fps : 6;
    const legacy = { size, frames: data.frames as (string | null)[][], fps } as PixelSpriteDoc;
    return { id: data.id, sprite: migratePixelSpriteDoc(legacy) };
  }

  fail('That sprite file has neither "clips" nor "frames" - nothing to import.');
}
